// src/components/Lane/Lane.js
import React, { useState } from "react";
import "./Lane.css";
import Box from "../Box/Box";
import DropPromptModal from "../DropPromptModal/DropPromptModal";
import apiService from "../../api";
import ConfirmModal from "../Common/ConfirmModal";
import { useConfirm } from "../../hooks/useConfirm";

const Lane = (props) => {
  const {
    lane,
    boxes = [],
    removeLane,
    selectedBoxes,
    onBoxClick,
    startLineDrawing,
    endLineDrawing,
    addBox,
    removeBox,
    updateBox,
    relations,
    entities = [],
    individuals = [],
    scenarioId,
    primaryIndividualId,
    onSetPrimary,
  } = props;

  // State for drop prompt modal
  const [dropModalOpen, setDropModalOpen] = useState(false);
  const [pendingDrop, setPendingDrop] = useState(null);

  // Confirm modal state
  const { confirmState, showConfirm, hideConfirm, handleConfirm } = useConfirm();

  const handleDrop = async (e) => {
    e.preventDefault();
    const boxId = e.dataTransfer.getData("boxId");
    const boxContent = e.dataTransfer.getData("boxContent");
    const boxHeaderColor = e.dataTransfer.getData("boxHeaderColor");
    const isTemplate = e.dataTransfer.getData("isTemplate") === "true";
    const templateType = e.dataTransfer.getData("templateType");

    if (boxes.length >= 4) {
      showConfirm({
        title: 'Limite atteinte',
        message: 'La limite de 4 éléments par voie a été atteinte.',
        confirmText: 'OK',
        cancelText: '',
        type: 'info',
        onConfirm: () => {}
      });
      return;
    }

    // Si c'est un template individu, montrer la modal de choix
    if (isTemplate && templateType === "individual") {
      const templateId = e.dataTransfer.getData("templateId");
      const templateFirstName = e.dataTransfer.getData("templateFirstName") || '';
      const templateLastName = e.dataTransfer.getData("templateLastName") || '';
      const templateName = `${templateFirstName} ${templateLastName}`.trim() || 'Personne';
      if (templateId) {
        // Check if there are existing individuals not yet on canvas
        const existingOnCanvas = boxes.filter(b => b.content === 'Individu').map(b => b.individual_id || b.id);
        const availableIndividuals = individuals.filter(ind =>
          !existingOnCanvas.includes(ind.individual_id || ind.id)
        );

        // If no existing individuals available, create directly
        if (availableIndividuals.length === 0) {
          await createIndividualFromTemplate(templateId, boxHeaderColor);
        } else {
          // Show modal with options
          setPendingDrop({
            type: 'individual',
            templateId,
            templateName,
            boxHeaderColor,
            existingItems: availableIndividuals
          });
          setDropModalOpen(true);
        }
        return;
      }
    }

    // Si c'est un template entité, montrer la modal de choix
    if (isTemplate && templateType === "entity") {
      const templateId = e.dataTransfer.getData("templateId");
      const templateName = e.dataTransfer.getData("templateEntityName") || 'Société';
      if (templateId) {
        // Check if there are existing entities not yet on canvas
        const existingOnCanvas = boxes.filter(b => b.content === 'Entity').map(b => b.entity_id || b.id);
        const availableEntities = entities.filter(ent =>
          !existingOnCanvas.includes(ent.entity_id || ent.id)
        );

        // If no existing entities available, create directly
        if (availableEntities.length === 0) {
          await createEntityFromTemplate(templateId, boxHeaderColor);
        } else {
          // Show modal with options
          setPendingDrop({
            type: 'entity',
            templateId,
            templateName,
            boxHeaderColor,
            existingItems: availableEntities
          });
          setDropModalOpen(true);
        }
        return;
      }
    }

    // Fallback pour les autres cas (boxes non-templates, drag interne, etc.)
    // Use temp ID format that will be replaced when saved
    const tempId = `temp-${Date.now()}`;
    const isIndividual = boxContent === 'Individu';
    const uniqueBoxId = isIndividual ? `individual-${tempId}` : `entity-${tempId}`;
    const newBox = {
      uniqueBoxId,
      boxId,
      content: boxContent,
      headerColor: boxHeaderColor,
      laneId: lane.id,
    };

    addBox(lane.id, newBox);
  };

  function handleDragOver(e) {
    e.preventDefault();
  }

  // Helper function to create individual from template
  const createIndividualFromTemplate = async (templateId, headerColor) => {
    try {
      const individual = await apiService.createIndividualFromTemplate({
        template_id: parseInt(templateId, 10),
        scenario_id: scenarioId,
        lane_position: lane.id,
        box_position: boxes.length
      });

      const newBox = {
        uniqueBoxId: `individual-${individual.individual_id}`,
        boxId: `individual-${individual.individual_id}`,
        id: individual.individual_id,
        individual_id: individual.individual_id,
        content: 'Individu',
        headerColor: headerColor || '#FF6F61',
        laneId: lane.id,
        first_name: individual.first_name,
        last_name: individual.last_name,
        marital_status: individual.marital_status,
        children_count: individual.children_count,
        single_parent: individual.single_parent,
        employment_type: individual.employment_type
      };

      addBox(lane.id, newBox);
    } catch (error) {
      console.error('Error creating individual from template:', error);
      showConfirm({
        title: 'Erreur',
        message: 'Erreur lors de la création de la personne.',
        confirmText: 'OK',
        cancelText: '',
        type: 'danger',
        onConfirm: () => {}
      });
    }
  };

  // Helper function to create entity from template
  const createEntityFromTemplate = async (templateId, headerColor) => {
    try {
      const entity = await apiService.createEntityFromTemplate({
        template_id: parseInt(templateId, 10),
        scenario_id: scenarioId,
        lane_position: lane.id,
        box_position: boxes.length
      });

      const newBox = {
        uniqueBoxId: `entity-${entity.entity_id}`,
        boxId: `entity-${entity.entity_id}`,
        id: entity.entity_id,
        entity_id: entity.entity_id,
        content: 'Entity',
        headerColor: headerColor || '#FFD700',
        laneId: lane.id,
        // CRITICAL: preserve template_id for proper linking
        template_id: entity.template_id,
        // Identity
        entity_name: entity.entity_name,
        entity_type: entity.entity_type,
        tax_regime: entity.tax_regime,
        activity_type: entity.activity_type,
        fiscal_year_end: entity.fiscal_year_end,
        share_capital: entity.share_capital,
        creation_date: entity.creation_date,
        // Managers
        managers: entity.managers ? JSON.stringify(entity.managers) : null,
        has_acre: entity.has_acre,
        has_cga: entity.has_cga,
        tva_regime: entity.tva_regime,
        dividend_percent: entity.dividend_percent,
        // Employees
        has_employees: entity.has_employees,
        employees_count: entity.employees_count,
        employees_avg_salary: entity.employees_avg_salary,
        employees_months: entity.employees_months,
        employee_profiles: entity.employee_profiles,
        // Charges
        charges_mode: entity.charges_mode,
        charges_data: entity.charges_data,
        // Fiscal params
        fiscal_params: entity.fiscal_params,
        advanced_options: entity.advanced_options,
        // CRITICAL: preserve forecast and exercice data
        forecast_start_date: entity.forecast_start_date,
        forecast_end_date: entity.forecast_end_date,
        forecastStartDate: entity.forecast_start_date,
        forecastEndDate: entity.forecast_end_date,
        exercice_data: entity.exercice_data
      };

      addBox(lane.id, newBox);
    } catch (error) {
      console.error('Error creating entity from template:', error);
      showConfirm({
        title: 'Erreur',
        message: 'Erreur lors de la création de la société.',
        confirmText: 'OK',
        cancelText: '',
        type: 'danger',
        onConfirm: () => {}
      });
    }
  };

  // Modal callback handlers
  const handleModalCreateNew = async () => {
    if (!pendingDrop) return;

    setDropModalOpen(false);
    if (pendingDrop.type === 'individual') {
      await createIndividualFromTemplate(pendingDrop.templateId, pendingDrop.boxHeaderColor);
    } else {
      await createEntityFromTemplate(pendingDrop.templateId, pendingDrop.boxHeaderColor);
    }
    setPendingDrop(null);
  };

  const handleModalUseExisting = (existingItem) => {
    if (!pendingDrop) return;

    setDropModalOpen(false);
    if (pendingDrop.type === 'individual') {
      const indId = existingItem.individual_id || existingItem.id;
      const newBox = {
        uniqueBoxId: `individual-${indId}`,
        boxId: `individual-${indId}`,
        id: indId,
        individual_id: indId,
        content: 'Individu',
        headerColor: pendingDrop.boxHeaderColor || '#FF6F61',
        laneId: lane.id,
        first_name: existingItem.first_name,
        last_name: existingItem.last_name,
        marital_status: existingItem.marital_status,
        children_count: existingItem.children_count,
        single_parent: existingItem.single_parent,
        employment_type: existingItem.employment_type
      };
      addBox(lane.id, newBox);
    } else {
      const entId = existingItem.entity_id || existingItem.id;
      const newBox = {
        uniqueBoxId: `entity-${entId}`,
        boxId: `entity-${entId}`,
        id: entId,
        entity_id: entId,
        content: 'Entity',
        headerColor: pendingDrop.boxHeaderColor || '#FFD700',
        laneId: lane.id,
        // CRITICAL: preserve template_id for proper linking
        template_id: existingItem.template_id,
        // Identity
        entity_name: existingItem.entity_name,
        entity_type: existingItem.entity_type,
        tax_regime: existingItem.tax_regime,
        activity_type: existingItem.activity_type,
        fiscal_year_end: existingItem.fiscal_year_end,
        share_capital: existingItem.share_capital,
        creation_date: existingItem.creation_date,
        // Managers
        managers: existingItem.managers ? JSON.stringify(existingItem.managers) : null,
        has_acre: existingItem.has_acre,
        has_cga: existingItem.has_cga,
        tva_regime: existingItem.tva_regime,
        dividend_percent: existingItem.dividend_percent,
        // Employees
        has_employees: existingItem.has_employees,
        employees_count: existingItem.employees_count,
        employees_avg_salary: existingItem.employees_avg_salary,
        employees_months: existingItem.employees_months,
        employee_profiles: existingItem.employee_profiles,
        // Charges
        charges_mode: existingItem.charges_mode,
        charges_data: existingItem.charges_data,
        // Fiscal params
        fiscal_params: existingItem.fiscal_params,
        advanced_options: existingItem.advanced_options,
        // CRITICAL: preserve forecast and exercice data
        forecast_start_date: existingItem.forecast_start_date,
        forecast_end_date: existingItem.forecast_end_date,
        forecastStartDate: existingItem.forecast_start_date,
        forecastEndDate: existingItem.forecast_end_date,
        exercice_data: existingItem.exercice_data
      };
      addBox(lane.id, newBox);
    }
    setPendingDrop(null);
  };

  const handleModalCancel = () => {
    setDropModalOpen(false);
    setPendingDrop(null);
  };

  const getIncomingRelations = (boxId) => {
    if (!Array.isArray(relations)) {
      console.warn("relations is not an array:", relations);
      return [];
    }
    return relations.filter(
      (rel) => rel.endBox.laneId === lane.id && rel.endBox.boxId === boxId
    );
  };

  return (
    <div
      className="lane"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      data-testid="lane"
      data-lane-id={lane.id}
    >
      <button className="remove-lane" onClick={() => {
        showConfirm({
          title: 'Supprimer cette voie ?',
          message: 'Voulez-vous vraiment supprimer cette voie et tous ses éléments ?',
          details: boxes.length > 0 ? [`Cette voie contient ${boxes.length} élément(s).`] : [],
          confirmText: 'Supprimer',
          type: 'danger',
          onConfirm: () => removeLane(lane.id)
        });
      }}>
        X
      </button>
      <div className="lane-info">
        Level {lane.level} (ID: {lane.id})
      </div>
      <div className="boxes-container">
        {boxes.map((box) => {
          const individualId = box.individual_id || box.id;
          const isIndividual = box.content === 'Individu';
          const isPrimary = isIndividual && individualId === primaryIndividualId;

          return (
            <Box
              key={box.uniqueBoxId}
              box={{
                ...box,
                incomingRelations: getIncomingRelations(box.uniqueBoxId),
              }}
              laneId={lane.id}
              scenarioId={scenarioId}
              onRemove={() => removeBox(lane.id, box.uniqueBoxId)}
              onClick={() => onBoxClick(lane.id, box.uniqueBoxId)}
              isSelected={selectedBoxes.some(
                (selectedBox) =>
                  selectedBox.laneId === lane.id &&
                  selectedBox.boxId === box.uniqueBoxId
              )}
              inSidebar={false}
              startLineDrawing={startLineDrawing}
              endLineDrawing={endLineDrawing}
              onUpdate={(boxId, updatedData) =>
                updateBox(lane.id, boxId, updatedData)
              }
              entities={entities}
              isPrimary={isPrimary}
              onSetPrimary={isIndividual ? onSetPrimary : undefined}
            />
          );
        })}
      </div>

      {/* Drop prompt modal */}
      <DropPromptModal
        isOpen={dropModalOpen}
        type={pendingDrop?.type}
        templateName={pendingDrop?.templateName}
        existingItems={pendingDrop?.existingItems || []}
        onCreateNew={handleModalCreateNew}
        onUseExisting={handleModalUseExisting}
        onCancel={handleModalCancel}
      />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={hideConfirm}
        onConfirm={handleConfirm}
        title={confirmState.title}
        message={confirmState.message}
        details={confirmState.details}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </div>
  );
};

export default Lane;